import { useContext, useMemo, useEffect } from "react";
import {
    ColDef,
    ValueFormatterParams,
    SetFilterValuesFuncParams,
    RowNode,
} from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import { map, filter, uniq, flatten } from "underscore";
import {
    PowerSkuCellRenderer,
    TagsCellRenderer,
    UniqueSkuIdCellRenderer,
    VcpmGlobalTagCellRenderer,
    CompliancePriceCellRenderer,
} from "@components/cell-renderers/AllCellRenderers";
import AgGridLayout from "@components/wrappers/layouts/AgGridLayout";
import { updateProductMappingFilterModel } from "@features/modalSlice";
import { 
    updateProductMappingSelectionData,
    updateProductMappingAllPagesSelected,
    updateProductMappingHeaderSelection
} from "@features/selectedDataSlice";
import {
    IMainContext,
    TCommonRowData,
    TClientTags,
    TagsMap,
    IPfmSkuFilterParams,
    GlobalTagRecordType,
    VcpmColumns,
    Features,
    ITagMappingStats,
} from "@ts/common-types";
import {
    customFilterOptions,
    mainContext,
    TAGS_MODULE_MAPPING,
    TAG_COLORS,
    TAG_MAPPING_STATS_DEFAULT_VALUE,
} from "@ts/constants";
import {
    numericValueFormatter,
    titleCase,
    getSortedTagTypeList,
    getActiveModule,
    getPfmColFilterValues,
    getProductMappingList,
    getProductMappingStats,
} from "@ts/utils";
import VcpmGlobalTagCellEditor from "@components/cell-renderers/VcpmGlobalTagCellEditor";
import { useGetBulkActionsMenuData } from "@hooks/useGetBulkActionsMenuData";
import { useMappingDataSource } from "@hooks/AllCustomHooks";
import MappingHeader from "./MappingHeader";
import SelectAllHeader from "@components/misc/SelectAllHeader";
import SelectionBanner from "@components/misc/SelectionBanner";

const ProductMapping = ({
    tagsMappingStats,
    setTagsMappingStats,
    showPfmTagsStatusModal,
    setShowPfmTagsStatusModal,
    clientIdOnChannelChange,
}: {
    tagsMappingStats: ITagMappingStats;
    setTagsMappingStats: (stats: ITagMappingStats) => void;
    showPfmTagsStatusModal: boolean;
    setShowPfmTagsStatusModal: (show: boolean) => void;
    clientIdOnChannelChange: string;
}) => {
    const {
        productMappingGridApi,
        setProductMappingGridApi,
        setProductMappingGridParams,
        productMappingGridLoading,
        setProductMappingGridLoading,
    } = useContext<IMainContext>(mainContext);

    const dispatch = useDispatch();

    const productMappingSelectionData = useSelector(
        (state: { productMappingSelectionData: { value: TCommonRowData } }) =>
            state.productMappingSelectionData.value
    );

    const productMappingAllPagesSelected = useSelector(
        (state: { productMappingAllPagesSelected: { value: boolean } }) =>
            state.productMappingAllPagesSelected.value
    );

    const productMappingHeaderSelection = useSelector(
        (state: { productMappingHeaderSelection: { value: boolean } }) =>
            state.productMappingHeaderSelection.value
    );

    const bulkPfmMappingCount = useSelector(
        (state: { bulkPfmMappingCount: { value: number } }) =>
            state.bulkPfmMappingCount.value
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const { activeModule } = getActiveModule();
    const { recordType } = TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];
    const sortedTagTypeList = useMemo(
        () => getSortedTagTypeList(tagTypeList, recordType),
        [tagTypeList, recordType]
    );

    const productMappingFilterModel = useSelector(
        (state: {
            productMappingFilterModel: { value: IPfmSkuFilterParams };
        }) => state.productMappingFilterModel.value
    );

    const bulkActionData = useGetBulkActionsMenuData();
    const filteredBulkActionData =
        Object.keys(productMappingFilterModel).length ||
            productMappingSelectionData.length
            ? bulkActionData
            : bulkActionData.filter(
                (item) => item.type !== Features.ADD_MAPPING
            );

    const getAvlTagsData = (colField: string) =>
        uniq(
            flatten(
                map(
                    filter(
                        sortedTagTypeList,
                        (tag) =>
                            tag.record_type === GlobalTagRecordType.GLOBAL &&
                            tag.tag_type.toLowerCase() === colField
                    ),
                    (tagType) =>
                        tagType.tags_map.map((tag) => {
                            const [tagId, tagName] = tag.split("##");
                            return {
                                tagId: tagId,
                                tagName: tagName,
                            };
                        })
                )
            )
        );

    const getTagTypeId = (colField: string) =>
        sortedTagTypeList.find((tag) => tag.tag_type.toLowerCase() === colField).tag_type_id;

    const onProductMappingSelectionChanged = () => {
        const selectedNodes = map(
            productMappingGridApi
                .getSelectedNodes()
                .filter((node) => node.displayed),
            (node) => node.data
        );

        dispatch(updateProductMappingSelectionData(selectedNodes));
        
        if (productMappingAllPagesSelected) {
            dispatch(updateProductMappingAllPagesSelected(false));
        }
        
        if (productMappingHeaderSelection && selectedNodes.length === 0) {
            dispatch(updateProductMappingHeaderSelection(false));
        }
    };

    const handleSelectAllCurrentPage = () => {
        if (!productMappingGridApi) return;
        
        dispatch(updateProductMappingHeaderSelection(true));
        
        const pageSize = productMappingGridApi.paginationGetPageSize();
        const currentPage = productMappingGridApi.paginationGetCurrentPage();
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        
        let currentIndex = 0;
        productMappingGridApi.forEachNode((rowNode: RowNode) => {
            if (rowNode.displayed) {
                // Only select nodes within current page range
                if (currentIndex >= startIndex && currentIndex < endIndex) {
                    rowNode.setSelected(true);
                }
                currentIndex++;
            }
        });
        
        dispatch(updateProductMappingAllPagesSelected(false));
    };

    const handleDeselectAll = () => {
        if (!productMappingGridApi) return;
        
        productMappingGridApi.deselectAll();
        dispatch(updateProductMappingSelectionData([]));
        dispatch(updateProductMappingAllPagesSelected(false));
        dispatch(updateProductMappingHeaderSelection(false));
    };

    const handleSelectAllPages = () => {
        dispatch(updateProductMappingAllPagesSelected(true));
    };

    const handleClearSelection = () => {
        handleDeselectAll();
    };

    const getColumnFilterValues = (
        params: SetFilterValuesFuncParams,
        recordField: string,
        isTagColumn = false
    ) => {
        const filterModel = productMappingGridApi?.getFilterModel() || {};
        const { [recordField]: currentField, ...restFilters } = filterModel;
        getPfmColFilterValues({
            filterModel: restFilters,
            recordField,
            ...(isTagColumn && { tagTypeId: getTagTypeId(recordField) })
        })
            .then((response) => {
                if (response) {
                    params.success(
                        [VcpmColumns.TAGS].includes(recordField as VcpmColumns)
                            ? flatten(response)
                            : response
                    );
                } else {
                    params.success([]);
                }
            })
            .catch((error: any) => {
                console.error(`Error fetching ${recordField} list:`, error);
                params.success([]);
            });
    };

    const productMappingDataSource = useMappingDataSource({
        setGridParams: setProductMappingGridParams,
        setGridLoading: setProductMappingGridLoading,
        setTagsMappingStats,
        getMappingStats: getProductMappingStats,
        getMappingList: getProductMappingList,
    });

    useEffect(() => {
        if (productMappingGridApi) {
            productMappingGridApi.refreshServerSideStore({ purge: true });
        }
    }, [clientIdOnChannelChange]);

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                field: "title",
                headerName: "Product",
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                checkboxSelection: true,
                headerComponent: SelectAllHeader,
                headerComponentParams: {
                    onSelectAllCurrentPage: handleSelectAllCurrentPage,
                    onDeselectAll: handleDeselectAll,
                },
                tooltipField: "sku_title",
                sortable: true,
                minWidth: 500,
                maxWidth: 700,
            },
            {
                field: "sku",
                headerName: "SKU",
                filter: "agSetColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                sortable: true,
                hide: true,
                minWidth: 200,
                maxWidth: 250,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, "sku"),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "channel_name",
                headerName: "Channel",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                sortable: true,
                valueFormatter: (param: ValueFormatterParams) =>
                    titleCase(param.value),
                filter: "agSetColumnFilter",
                minWidth: 120,
                maxWidth: 150,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, "channel_name"),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "category",
                headerName: "Category",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: VcpmGlobalTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: VcpmGlobalTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(
                        VcpmColumns.CATEGORY.toLowerCase()
                    ),
                }),
                filter: "agSetColumnFilter",
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background:
                        TAG_COLORS[0].background,
                },
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.CATEGORY.toLowerCase(),
                            true
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
                suppressKeyboardEvent: (params) => {
                    const key = params.event.key;
                    return ["Enter"].includes(key);
                },
            },
            {
                field: "subcategory",
                headerName: "Subcategory",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: VcpmGlobalTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: VcpmGlobalTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(
                        VcpmColumns.SUBCATEGORY.toLowerCase()   
                    ),
                }),
                filter: "agSetColumnFilter",
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background:
                        TAG_COLORS[1].background,
                },
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.SUBCATEGORY.toLowerCase(),
                            true
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
                suppressKeyboardEvent: (params) => {
                    const key = params.event.key;
                    return ["Enter"].includes(key);
                },
            },
            {
                field: "brand",
                headerName: "Brand",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: VcpmGlobalTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: VcpmGlobalTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(
                        VcpmColumns.BRAND.toLowerCase()
                    ),
                }),
                filter: "agSetColumnFilter",
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background:
                        TAG_COLORS[2].background,
                },
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.BRAND.toLowerCase(),
                            true
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
                suppressKeyboardEvent: (params) => {
                    const key = params.event.key;
                    return ["Enter"].includes(key);
                },
            },
            {
                field: "tags_map",
                headerName: "Product Tags",
                filter: "agSetColumnFilter",
                minWidth: 250,
                tooltipField: "sku_tags",
                cellRenderer: TagsCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, VcpmColumns.TAGS),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "sku_identifier",
                headerName: "Product ID",
                filter: "agSetColumnFilter",
                cellRenderer: UniqueSkuIdCellRenderer,
                minWidth: 250,
                maxWidth: 270,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, VcpmColumns.SKU_ID),
                },
            },
            {
                field: "power_sku",
                headerName: "Power SKU",
                filter: "agSetColumnFilter",
                cellRenderer: PowerSkuCellRenderer,
                minWidth: 150,
                maxWidth: 150,
                filterParams: {
                    values: [true, false],
                },
            },
            {
                field: "compliance_price",
                headerName: "Compliance Price",
                filter: "agNumberColumnFilter",
                cellRenderer: CompliancePriceCellRenderer,
                minWidth: 190,
                maxWidth: 200,
            },
            {
                field: "avg_price",
                headerName: "Price",
                sortable: true,
                valueFormatter: numericValueFormatter,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                },
            },
            {
                field: "upd",
                headerName: "Sales Index",
                sortable: true,
                valueFormatter: numericValueFormatter,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                },
            },
            {
                field: "organic_impressions",
                headerName: "Visibility Index",
                sortable: true,
                sort: "desc",
                valueFormatter: numericValueFormatter,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                },
            },
            {
                field: "category_1ds",
                headerName: "1DS Category",
                sortable: true,
                filter: "agSetColumnFilter",
                minWidth: 250,
                maxWidth: 260,
                hide: true,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.CATEGORY_1DS.toLowerCase()
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "subcategory_1ds",
                headerName: "1DS Subcategory",
                sortable: true,
                filter: "agSetColumnFilter",
                hide: true,
                minWidth: 250,
                maxWidth: 260,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.SUBCATEGORY_1DS.toLowerCase()
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "brand_1ds",
                headerName: "1DS Brand",
                sortable: true,
                filter: "agSetColumnFilter",
                hide: true,
                minWidth: 250,
                maxWidth: 260,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.BRAND_1DS.toLowerCase()
                        ),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true,
                },
            },
            {
                field: "pack_size_band",
                headerName: "Pack Size Band",
                sortable: true,
                filter: "agSetColumnFilter",
                hide: true,
                minWidth: 250,
                maxWidth: 260,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(
                            params,
                            VcpmColumns.PACK_SIZE_BAND
                        ),
                },
            },
            {
                field: "price_band",
                headerName: "Price Band",
                sortable: true,
                filter: "agSetColumnFilter",
                hide: true,
                minWidth: 250,
                maxWidth: 260,
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, VcpmColumns.PRICE_BAND),
                },
            },
            {
                field: "is_combo",
                headerName: "Is Combo",
                sortable: true,
                filter: "agSetColumnFilter",
                hide: true,
                minWidth: 250,
                maxWidth: 260,
                filterParams: {
                    values: [true, false],
                },
            },
        ],
        [sortedTagTypeList, productMappingGridApi, productMappingSelectionData, productMappingAllPagesSelected]
    );

    // Calculate selection count for banner
    const getSelectionCount = () => {
        if (productMappingAllPagesSelected) {
            return bulkPfmMappingCount;
        }
        return productMappingSelectionData.length;
    };

    useEffect(() => {
        if (productMappingGridApi) {
            const handleFilterChanged = () => {
                const filterModel = productMappingGridApi.getFilterModel();
                dispatch(updateProductMappingFilterModel(filterModel || {}));
            };

            productMappingGridApi.addEventListener(
                "filterChanged",
                handleFilterChanged
            );

            return () => {
                productMappingGridApi.removeEventListener(
                    "filterChanged",
                    handleFilterChanged
                );
            };
        }
        return () => {
            dispatch(updateProductMappingSelectionData([]));
            dispatch(updateProductMappingFilterModel({}));
        };
    }, [productMappingGridApi, dispatch]);

    return (
        <>
            <div>
                <MappingHeader
                    title="Product Mapping"
                    gridApi={productMappingGridApi}
                    gridLoading={productMappingGridLoading}
                    filterModel={productMappingFilterModel}
                    tagsMappingStats={tagsMappingStats}
                    bulkActionData={filteredBulkActionData}
                    onFilterModelUpdate={updateProductMappingFilterModel}
                    onShowStatusModal={() => setShowPfmTagsStatusModal(true)}
                    isRefreshEnabled={true}
                />

                <SelectionBanner
                    selectedCount={getSelectionCount()}
                    totalCount={bulkPfmMappingCount}
                    isAllPagesSelected={productMappingAllPagesSelected}
                    isHeaderSelection={productMappingHeaderSelection}
                    onSelectAllPages={handleSelectAllPages}
                    onClearSelection={handleClearSelection}
                    itemType="Products"
                />

                <div className="border-top pt-2 mt-2" />
                <div
                    id="product-mapping-grid"
                    className="ag-theme-alpine product-mapping-grid"
                    style={{
                        height: productMappingHeaderSelection 
                            ? "calc(100vh - 300px)" 
                            : "calc(100vh - 250px)", 
                        width: "100%",
                        flex: "1",
                    }}
                >
                    <AgGridLayout
                        api={productMappingGridApi}
                        loading={productMappingGridLoading}
                        onSelectionChanged={onProductMappingSelectionChanged}
                        setGridApi={setProductMappingGridApi}
                        columnDefs={columnDefs}
                        isRowSelectable={() => true}
                        rowHeight={48}
                        pagination={true}
                        rowModelType="serverSide"
                        serverSideDatasource={productMappingDataSource}
                        serverSideStoreType="partial"
                        loadingCellRenderer={() => null}
                        paginationPageSize={500}
                        cacheBlockSize={2500}
                        maxBlocksInCache={5}
                        isServerSideGrid={true}
                    />
                </div>
            </div>
        </>
    );
};

export default ProductMapping;
